import { C } from './constants';

/**
 * Position badge — compact pill showing player position abbreviation.
 *
 * Supports both basketball and baseball position strings. Baseball positions
 * are auto-detected from the set below and get a role-colored pill (pitchers,
 * catchers, infielders, outfielders, utility). Anything not recognized falls
 * through to the legacy basketball look (gold text on goldDim).
 *
 * The `sport` prop forces a behavior:
 *   - `sport="baseball"`: always use the baseball color map (default when the
 *     position string matches a baseball position code)
 *   - `sport="basketball"`: always use the legacy gold-on-goldDim pill
 *
 * If `sport` is not supplied, we auto-detect from the position string.
 *
 * @param {string} pos - Position string (e.g., "PG", "SS", "1B", "CF", "SF/PF")
 * @param {"baseball"|"basketball"} [sport] - Force a color map
 */

// Canonical baseball position codes (MLB scorecard abbreviations + utility).
const BASEBALL_POSITIONS = new Set([
  'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'UTIL', 'OF', 'IF',
]);

// Role groupings → (background, foreground) colors.
// Pitchers use the team primary token so the pill re-themes per team.
// Catchers, infielders, outfielders, utility are role-stable across teams.
const BASEBALL_ROLE_COLORS = {
  pitcher:   { bg: 'color-mix(in srgb, var(--team-primary) 20%, transparent)', fg: 'var(--team-primary)' },
  catcher:   { bg: 'rgba(184, 74, 74, 0.20)',  fg: '#B84A4A' },
  infield:   { bg: 'rgba(74, 143, 184, 0.20)', fg: '#4A8FB8' },
  outfield:  { bg: 'rgba(91, 166, 114, 0.20)', fg: '#5BA672' },
  utility:   { bg: 'rgba(136, 136, 136, 0.20)', fg: '#888888' },
};

function baseballRoleFor(code) {
  if (code === 'P') return 'pitcher';
  if (code === 'C') return 'catcher';
  if (code === '1B' || code === '2B' || code === '3B' || code === 'SS' || code === 'IF') return 'infield';
  if (code === 'LF' || code === 'CF' || code === 'RF' || code === 'OF') return 'outfield';
  if (code === 'DH' || code === 'UTIL') return 'utility';
  return null;
}

export const PosBadge = ({ pos, sport }) => {
  const raw = typeof pos === 'string' ? pos : '';
  // Normalize for detection — uppercase, strip whitespace.
  const upper = raw.trim().toUpperCase();

  // Auto-detect baseball if not forced. Multi-position strings like "SS/2B"
  // count if the first token matches.
  const firstToken = upper.split(/[\/,\s-]/)[0];
  const isBaseball =
    sport === 'baseball' ||
    (sport !== 'basketball' && BASEBALL_POSITIONS.has(firstToken));

  let background = C.goldDim;
  let color = C.gold;

  if (isBaseball) {
    const role = baseballRoleFor(firstToken);
    if (role) {
      const palette = BASEBALL_ROLE_COLORS[role];
      background = palette.bg;
      color = palette.fg;
    }
  }

  // Label: if baseball, preserve full code (1B, SS, CF — up to 2-char code,
  // 1B/2B/3B still fit in 2 chars). Legacy basketball behavior truncated to 2.
  const label = isBaseball
    ? firstToken || upper.slice(0, 2)
    : upper.length > 2 ? upper.substring(0, 2) : upper;

  return (
    <span
      data-role={isBaseball ? baseballRoleFor(firstToken) || 'unknown' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 22,
        borderRadius: 6,
        background,
        color,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.5,
      }}
    >
      {label}
    </span>
  );
};
